/**
 * AP Computer Science Principles course. A separate progression from the
 * classic Stanford units, registered under the `apcsp` course so its unit
 * numbers restart at 1. Lessons use the AP CSP-aligned Karel API: relative
 * `canMove(direction)`, integer `random(a, b)`, lists, procedures, and
 * input/output with `input()` / `print()`.
 */
import { IdeDemo, IdeLesson } from "../learn/engine/lessonsModel";
import {
  registerUnit,
  type LessonFactory,
  type UnitSnippets,
} from "../learn/engine/lessonRegistry";

const SNIPPETS: Record<string, UnitSnippets> = {
  algorithms: {
    js: `// Algorithms
move();
turnLeft();
putBeeper();`,
    python: `# Algorithms
move()
turn_left()
put_beeper()`,
  },
  variables: {
    js: `let n = random(3, 6);
for (let i = 0; i < n; i++) {
  move();
}`,
    python: `n = random(3, 6)
for i in range(n):
    move()`,
  },
  procedures: {
    js: `function moveN(n) {
  for (let i = 0; i < n; i++) {
    move();
  }
}`,
    python: `def move_n(n):
    for i in range(n):
        move()`,
  },
  selection: {
    js: `if (canMove("forward")) {
  move();
}`,
    python: `if can_move("forward"):
    move()`,
  },
  lists: {
    js: `let targets = [2, 4, 6];
for (let i = 0; i < length(targets); i++) {
  move();
}`,
    python: `targets = [2, 4, 6]
for target in targets:
    move()`,
  },
  io: {
    js: `let n = int(input());
print(n);`,
    python: `n = int(input())
print(n)`,
  },
  capstone: {
    js: `function moveN(n) {
  for (let i = 0; i < n; i++) {
    move();
  }
}`,
    python: `def move_n(n):
    for i in range(n):
        move()`,
  },
};

// Unit 1 -- Algorithms: sequencing and abstraction.
const c1l1: LessonFactory = () => IdeDemo("apcsp/c1l1");
const c1l2: LessonFactory = () => IdeLesson("apcsp/c1l2");

// Unit 2 -- Variables and random numbers.
const c2l1: LessonFactory = () => IdeLesson("apcsp/c2l1");
const c2l2: LessonFactory = () => IdeLesson("apcsp/c2l2");

// Unit 3 -- Procedures: parameters and return values.
const c3l1: LessonFactory = () => IdeLesson("apcsp/c3l1");
const c3l2: LessonFactory = () =>
  IdeLesson("apcsp/c3l2", { "apcsp/c3l2": { output: "7" } }, undefined, true);

// Unit 4 -- Selection with canMove.
const c4l1: LessonFactory = () => IdeLesson("apcsp/c4l1");
const c4l2: LessonFactory = () => IdeLesson("apcsp/c4l2");

// Unit 5 -- Iteration and lists.
const c5l1: LessonFactory = () => IdeLesson("apcsp/c5l1");
const c5l2: LessonFactory = () => IdeLesson("apcsp/c5l2");
const c5l3: LessonFactory = () => IdeDemo("apcsp/c5l3");

// Unit 6 -- Input and output.
const c6l1: LessonFactory = () =>
  IdeLesson(
    "apcsp/c6l1",
    { "apcsp/c6l1": { inputs: ["4"], goal: "apcsp/c6l1.goal" } },
    undefined,
    true,
  );
const c6l2: LessonFactory = () =>
  IdeLesson(
    "apcsp/c6l2",
    { "apcsp/c6l2": { inputs: ["3", "4"], output: "7" } },
    undefined,
    true,
  );

// Unit 7 -- Capstone: combine procedures, selection, and lists.
const c7l1: LessonFactory = () => IdeLesson("apcsp/c7l1");

export function registerApCspLessons(): void {
  registerUnit("Algorithms", [c1l1, c1l2], SNIPPETS.algorithms, "apcsp");
  registerUnit("Variables", [c2l1, c2l2], SNIPPETS.variables, "apcsp");
  registerUnit("Procedures", [c3l1, c3l2], SNIPPETS.procedures, "apcsp");
  registerUnit("Selection", [c4l1, c4l2], SNIPPETS.selection, "apcsp");
  registerUnit("Lists", [c5l1, c5l2, c5l3], SNIPPETS.lists, "apcsp");
  registerUnit("Input and Output", [c6l1, c6l2], SNIPPETS.io, "apcsp");
  registerUnit("Capstone", [c7l1], SNIPPETS.capstone, "apcsp");
}
